/**
 * Part 16 — Custom status sets client.
 *
 * Backend at /api/v1/status-sets/. Sets group statuses by entity_type
 * ("project" | "task"). When an org has a default set we use it; otherwise
 * callers fall back to the canonical enum values via useStatusOptions.
 */
import { apiClient } from './api'

export type StatusEntityType = 'project' | 'task'

export const CANONICAL_PROJECT = [
  'planning',
  'active',
  'on_hold',
  'completed',
  'cancelled',
] as const
export const CANONICAL_TASK = [
  'todo',
  'in_progress',
  'review',
  'done',
  'blocked',
] as const
export type CanonicalProjectStatus = (typeof CANONICAL_PROJECT)[number]
export type CanonicalTaskStatus = (typeof CANONICAL_TASK)[number]
export type CanonicalStatus = CanonicalProjectStatus | CanonicalTaskStatus

export interface OrgStatus {
  id?: number
  key: string
  label: string
  color?: string | null
  position: number
  canonical: string
  is_terminal: boolean
}

export interface OrgStatusSet {
  id: number
  organization_id: number | null
  entity_type: StatusEntityType
  name: string
  description: string | null
  is_default: boolean
  created_at: string
  updated_at: string
  statuses: OrgStatus[]
}

export interface OrgStatusSetCreate {
  entity_type: StatusEntityType
  name: string
  description?: string | null
  is_default?: boolean
  organization_id?: number | null
  statuses: OrgStatus[]
}

export interface OrgStatusSetUpdate {
  name?: string
  description?: string | null
  is_default?: boolean
  statuses?: OrgStatus[]
}

export const statusSetsService = {
  list: async (params?: {
    entity_type?: StatusEntityType
    organization_id?: number
  }): Promise<OrgStatusSet[]> => {
    const { data } = await apiClient.get<OrgStatusSet[]>('/api/v1/status-sets/', {
      params,
    })
    return data
  },
  get: async (id: number): Promise<OrgStatusSet> => {
    const { data } = await apiClient.get<OrgStatusSet>(`/api/v1/status-sets/${id}`)
    return data
  },
  create: async (input: OrgStatusSetCreate): Promise<OrgStatusSet> => {
    const { data } = await apiClient.post<OrgStatusSet>('/api/v1/status-sets/', input)
    return data
  },
  update: async (
    id: number,
    patch: OrgStatusSetUpdate,
  ): Promise<OrgStatusSet> => {
    const { data } = await apiClient.put<OrgStatusSet>(
      `/api/v1/status-sets/${id}`,
      patch,
    )
    return data
  },
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/v1/status-sets/${id}`)
  },
}

// ── Canonical fallback labels ─────────────────────────────────────────────
// Used by useStatusOptions when an org has not defined its own set.
export const CANONICAL_PROJECT_LABELS: Record<CanonicalProjectStatus, string> = {
  planning: 'Planning',
  active: 'Active',
  on_hold: 'On Hold',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

export const CANONICAL_TASK_LABELS: Record<CanonicalTaskStatus, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
  blocked: 'Blocked',
}

export function canonicalOptionsFor(
  entityType: StatusEntityType,
): OrgStatus[] {
  if (entityType === 'project') {
    return CANONICAL_PROJECT.map((c, i) => ({
      key: c,
      label: CANONICAL_PROJECT_LABELS[c],
      canonical: c,
      position: i,
      is_terminal: c === 'completed' || c === 'cancelled',
      color: null,
    }))
  }
  return CANONICAL_TASK.map((c, i) => ({
    key: c,
    label: CANONICAL_TASK_LABELS[c],
    canonical: c,
    position: i,
    is_terminal: c === 'done',
    color: null,
  }))
}
